import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeepCompareEffect } from '@anytag/shared-hooks';
import { InfluencerProfile } from '@anytag/shared-organisms';
import { formatIntNumber } from '@anytag/shared-utils';
import { useSelectInfluencersMapper } from './use-select-influencers-mapper';
import { useToggleSelectedInfluencers } from './use-toggle-selected-influencers';

export const useAllInfluencersSelectForPackage = () => {
  const { t } = useTranslation();
  const { toggleSelectedInfluencer } = useToggleSelectedInfluencers();
  const { selectedInfluencersMapIds } = useSelectInfluencersMapper();

  const [togglingState, setTogglingState] = useState<{ id: number | null; loading: boolean }>({
    id: null,
    loading: false
  });

  const resetLoadingState = () => {
    setTogglingState({ id: null, loading: false });
  };

  useDeepCompareEffect(() => {
    resetLoadingState();
  }, [selectedInfluencersMapIds]);

  const getSortSelectButtonProps = (listRecords: readonly InfluencerProfile[], totalRecords: number) => {
    const allInfluencerIdsInPage = listRecords.map((infl) => infl.id);
    const selectedAccountsInPage = listRecords.reduce<number[]>(
      (rs, infl) => (selectedInfluencersMapIds.has(infl.id) && !rs.includes(infl.id) ? [...rs, infl.id] : rs),
      []
    );
    const listRecordsLength = listRecords.length;
    const loading = !togglingState.id && togglingState.loading;
    const numOfSelectedAccountsInPage = selectedAccountsInPage.length;
    const hasAtLeastOneAccountSelectedInPage = numOfSelectedAccountsInPage > 0;
    const hasAllAccountsSelectedInPage = listRecordsLength > 0 && selectedAccountsInPage.length >= listRecordsLength;

    return {
      isInfluencerAccount: true,
      selectedAccountsInPage,
      loading,
      onClick: async () => {
        setTogglingState({ id: null, loading: true });
        const { error } = await toggleSelectedInfluencer(allInfluencerIdsInPage, hasAllAccountsSelectedInPage);
        if (error) {
          resetLoadingState();
        }
      },
      disabled: loading || listRecords.length === 0,
      checked: hasAtLeastOneAccountSelectedInPage,
      indeterminate: numOfSelectedAccountsInPage !== listRecordsLength,
      label: hasAtLeastOneAccountSelectedInPage
        ? t('numberInfluencersAddedToProposal', { count: numOfSelectedAccountsInPage })
        : t('Accounts found', { count: formatIntNumber(totalRecords) as any })
    };
  };

  const getListSelectButtonProps = ({ id }: InfluencerProfile) => {
    const isSelected = selectedInfluencersMapIds?.has(id) || false;
    const loading = togglingState.id === id && togglingState.loading;

    return {
      loading,
      checked: isSelected,
      disabled: loading,
      onClick: async () => {
        setTogglingState({ id, loading: true });
        const { error } = await toggleSelectedInfluencer([id], isSelected);
        if (error) {
          resetLoadingState();
        }
      }
    };
  };

  return {
    getSortSelectButtonProps,
    getListSelectButtonProps
  };
};
